"use client";

import { useCallback, useMemo, useState } from "react";
import {
  Button,
  HStack,
  Spinner,
  Text,
  type ButtonProps,
} from "@chakra-ui/react";
import type { FullViewResume } from "@/_models/resume";
import { downloadAtsResumePdf } from "./ATSResumePdfExport";

export interface AtsResumePdfDownloadButtonProps extends ButtonProps {
  data: FullViewResume;
  /** Defaults to `{name}_Resume.pdf` */
  filename?: string;
  onExportStart?: () => void;
  onExportSuccess?: (filename: string) => void;
  onExportError?: (error: Error) => void;
}

/**
 * Standalone download control using @react-pdf/renderer (`DefaultAtsPdfDocument`).
 * Use when you need PDF export outside of {@link ATSResume} layout.
 */
export function AtsResumePdfDownloadButton({
  data,
  filename: filenameProp,
  onExportStart,
  onExportSuccess,
  onExportError,
  children,
  disabled,
  ...buttonProps
}: AtsResumePdfDownloadButtonProps) {
  const [loading, setLoading] = useState(false);

  const filename = useMemo(
    () =>
      filenameProp ??
      `${data.resume.job_details.basic_info.name.replace(/\s+/g, "_")}_Resume.pdf`,
    [data, filenameProp],
  );

  const handleClick = useCallback(async () => {
    try {
      setLoading(true);
      onExportStart?.();
      await downloadAtsResumePdf(data, filename);
      onExportSuccess?.(filename);
    } catch (e) {
      onExportError?.(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [data, filename, onExportError, onExportStart, onExportSuccess]);

  return (
    <Button
      type="button"
      loading={loading}
      disabled={disabled || loading}
      onClick={handleClick}
      aria-label="Download resume as PDF"
      {...buttonProps}
    >
      {loading ? (
        <HStack gap={2}>
          <Spinner size="sm" />
          <Text as="span">Exporting…</Text>
        </HStack>
      ) : (
        (children ?? "Download PDF")
      )}
    </Button>
  );
}
